import { z } from 'zod';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { normaliseFirmwareVersion } from '@evimo/types/firmware/firmware-version';
import type {
  DiagnosticRule,
  DiagnosticRuleIndexingStatus,
  DiagnosticRuleKind,
  DiagnosticRuleOptions,
} from '~/app/services/diagnosticRules/diagnostic-rule.types';
import { DiagnosticRuleHttpError } from '~/app/services/diagnosticRules/diagnostic-rule-error';

export const RULE_TITLE_MAX = 200;
export const RULE_DESCRIPTION_MAX = 10_000;
export const RULE_FIRMWARE_MAX = 100;
export const RULE_MAX_FIRMWARES = 20;
export const RULE_MAX_ASSET_MODELS = 50;
export const RULE_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const RULE_FILE_EXTENSIONS = ['.pdf', '.txt', '.md'] as const;
export const RULE_FILE_ACCEPT = RULE_FILE_EXTENSIONS.join(',');
export const RULES_POLL_INTERVAL_MS = 3000;

export type RuleFormErrorKey =
  | 'form.errors.titleRequired'
  | 'form.errors.titleTooLong'
  | 'form.errors.descriptionRequired'
  | 'form.errors.descriptionTooLong'
  | 'form.errors.firmwareTooLong'
  | 'form.errors.firmwareInvalid'
  | 'form.errors.firmwareTooMany'
  | 'form.errors.modelsTooMany'
  | 'form.errors.fileRequired'
  | 'form.errors.fileType'
  | 'form.errors.fileTooLarge';

export type RuleFormValues = {
  kind: DiagnosticRuleKind;
  title: string;
  description: string;
  assetModelIds?: string[];
  firmwareVersions?: string[];
};

const ruleFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'form.errors.titleRequired')
    .max(RULE_TITLE_MAX, 'form.errors.titleTooLong'),
  description: z
    .string()
    .trim()
    .min(1, 'form.errors.descriptionRequired')
    .max(RULE_DESCRIPTION_MAX, 'form.errors.descriptionTooLong'),
  assetModelIds: z
    .array(z.string())
    .max(RULE_MAX_ASSET_MODELS, 'form.errors.modelsTooMany')
    .optional(),
  firmwareVersions: z
    .array(
      z
        .string()
        .trim()
        .max(RULE_FIRMWARE_MAX, 'form.errors.firmwareTooLong')
        .refine(
          (value) => normaliseFirmwareVersion(value) !== '',
          'form.errors.firmwareInvalid',
        ),
    )
    .refine(
      (values) => uniqueFirmwares(values).length <= RULE_MAX_FIRMWARES,
      'form.errors.firmwareTooMany',
    )
    .optional(),
});

/** Field errors as i18n keys (the API checks the same rules). */
export function validateRuleForm(
  values: RuleFormValues,
  file: { name: string; size: number } | null,
  options: { requireFile: boolean },
): Partial<Record<keyof RuleFormValues | 'file', RuleFormErrorKey>> {
  const errors: Partial<
    Record<keyof RuleFormValues | 'file', RuleFormErrorKey>
  > = {};
  const parsed = ruleFormSchema.safeParse(values);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0] as keyof RuleFormValues;
      errors[field] ??= issue.message as RuleFormErrorKey;
    }
  }
  if (values.kind === 'DOCUMENT') {
    const fileError = file
      ? validateRuleFile(file)
      : options.requireFile
        ? 'form.errors.fileRequired'
        : null;
    if (fileError) errors.file = fileError;
  }
  return errors;
}

export function validateRuleFile(file: {
  name: string;
  size: number;
}): RuleFormErrorKey | null {
  const name = file.name.toLowerCase();
  if (!RULE_FILE_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return 'form.errors.fileType';
  }
  return file.size > RULE_FILE_MAX_BYTES ? 'form.errors.fileTooLarge' : null;
}

/** Normalised versions without duplicates, in typing order (like the API). */
function uniqueFirmwares(values: readonly string[]): string[] {
  const normalised = values.map(normaliseFirmwareVersion).filter(Boolean);
  return [...new Set(normalised)];
}

/** "Saved as 1.2.3, 2.0" preview, same normaliser as the API. */
export function firmwarePreview(values: readonly string[]): string | null {
  return uniqueFirmwares(values).join(', ') || null;
}

/**
 * Models offered for a manufacturer. When the catalog links no model to it,
 * every model is offered (the catalog is incomplete).
 */
export function modelsForManufacturer(
  models: DiagnosticRuleOptions['assetModels'],
  manufacturerId: string | undefined,
): { models: DiagnosticRuleOptions['assetModels']; filtered: boolean } {
  if (!manufacturerId) return { models, filtered: false };
  const linked = models.filter((model) =>
    model.manufacturerIds.includes(manufacturerId),
  );
  return linked.length > 0
    ? { models: linked, filtered: true }
    : { models, filtered: false };
}

/** Firmwares reported by chargers of the chosen manufacturer and models. */
export function firmwareSuggestions(
  firmwares: DiagnosticRuleOptions['firmwares'],
  manufacturerId: string | undefined,
  assetModelIds: readonly string[],
): string[] {
  const versions = firmwares
    .filter(
      (firmware) =>
        (!manufacturerId || firmware.manufacturerId === manufacturerId) &&
        (assetModelIds.length === 0 ||
          (firmware.assetModelId !== null &&
            assetModelIds.includes(firmware.assetModelId))),
    )
    .map((firmware) => firmware.version);
  return [...new Set(versions)].sort();
}

/**
 * A super admin working in another tenant's scope must ask for the audited
 * cross-tenant bypass; the global scope and the own organization need none.
 */
export function needsCrossTenantBypass(
  isSuperAdmin: boolean,
  scope: string,
  ownOrganizationId: string | null | undefined,
): boolean {
  return isSuperAdmin && scope !== 'global' && scope !== ownOrganizationId;
}

/** "A, B" or "-" for an empty list (table cells). */
export function formatList(values: readonly string[] | undefined): string {
  return values && values.length > 0 ? values.join(', ') : '-';
}

export function indexingStatusTag(status: DiagnosticRuleIndexingStatus): {
  color: string;
  labelKey: string;
  spinning: boolean;
} {
  switch (status) {
    case 'INDEXED':
      return { color: 'success', labelKey: 'status.indexed', spinning: false };
    case 'FAILED':
      return { color: 'error', labelKey: 'status.failed', spinning: false };
    default:
      return {
        color: 'processing',
        labelKey: 'status.pending',
        spinning: true,
      };
  }
}

/** Poll while a rule is being indexed; never on a 401 (a new login is needed). */
export function rulesRefetchInterval(
  rules: readonly Pick<DiagnosticRule, 'indexingStatus'>[] | undefined,
  error: Error | null,
): number | false {
  if (error instanceof DiagnosticRuleHttpError && error.status === 401) {
    return false;
  }
  return rules?.some((rule) => rule.indexingStatus === 'PENDING')
    ? RULES_POLL_INTERVAL_MS
    : false;
}

export function ruleAbilities(hasPermission: (key: PermissionKey) => boolean): {
  canRead: boolean;
  canManage: boolean;
} {
  return {
    canRead: hasPermission(PermissionKey.DIAGNOSTIC_RULES_READ),
    canManage: hasPermission(PermissionKey.DIAGNOSTIC_RULES_MANAGE),
  };
}
